import { useState, useEffect } from "react";
import { escapeHtml, escapeHtmlWithBreaks } from "@/utils/escapeHtml";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Copy,
  Check,
  Printer,
  User,
  Mail,
  Phone,
  MapPin,
  Package,
  Truck,
  Loader2,
  StickyNote,
  Tag,
  Clock,
  AlertTriangle,
  Send,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

export interface DeliveryAddress {
  line1?: string;
  line2?: string;
  city?: string;
  county?: string;
  postcode?: string;
  country?: string;
}

export interface Order {
  id: string;
  order_number: string;
  created_at: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  delivery_address: DeliveryAddress;
  items: OrderItem[];
  subtotal: number;
  shipping: number;
  total: number;
  status: string;
  payment_status: string;
  fulfilled: boolean;
  fulfilled_at?: string | null;
  dispatch_at?: string | null;
  tracking_number?: string;
  courier?: string;
  internal_notes?: string;
}

// UK date/time formatter
export const formatUKDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).replace(",", " •");
};

interface OrderDetailModalProps {
  order: Order | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (orderId: string, updates: Partial<Order>) => Promise<void>;
}

const COURIERS = [
  { value: "royal_mail", label: "Royal Mail" },
  { value: "royal_mail_tracked", label: "Royal Mail Tracked 24" },
  { value: "royal_mail_tracked_48", label: "Royal Mail Tracked 48" },
  { value: "evri", label: "Evri" },
  { value: "evri_next_day", label: "Evri Next Day" },
  { value: "dpd", label: "DPD" },
  { value: "dpd_next_day", label: "DPD Next Day" },
  { value: "hermes", label: "Hermes" },
  { value: "yodel", label: "Yodel" },
  { value: "parcelforce", label: "Parcelforce" },
  { value: "other", label: "Other" },
];

export function OrderDetailModal({
  order,
  open,
  onOpenChange,
  onUpdate,
}: OrderDetailModalProps) {
  const { session } = useAuth();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [courier, setCourier] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Sync local state with order when it changes
  useEffect(() => {
    if (order) {
      setTrackingNumber(order.tracking_number || "");
      setCourier(order.courier || "");
      setInternalNotes(order.internal_notes || "");
    }
  }, [order]);

  if (!order) return null;

  const formatAddressMultiline = (addr: DeliveryAddress) => {
    return [
      addr.line1,
      addr.line2,
      addr.city,
      addr.county,
      addr.postcode,
      addr.country,
    ]
      .filter(Boolean)
      .join("\n");
  };

  // Generate full shipping label format
  const generateShippingLabel = () => {
    const addr = order.delivery_address;
    return `${order.customer_name}
${addr.line1 || ""}
${addr.line2 ? addr.line2 + "\n" : ""}${addr.city || ""}
${addr.county || ""}
${addr.postcode || ""}
${addr.country || ""}`.trim();
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  // Combined Fulfil Order flow
  const handleFulfilOrder = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(order.id, { 
        fulfilled: true, 
        tracking_number: trackingNumber || undefined,
        courier: courier || undefined,
      });
      toast.success("Order fulfilled successfully");
    } catch {
      toast.error("Failed to fulfil order");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMarkUnfulfilled = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(order.id, { fulfilled: false });
      toast.success("Marked as unfulfilled");
    } catch {
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveTracking = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(order.id, { tracking_number: trackingNumber, courier });
      toast.success("Tracking info saved");
    } catch {
      toast.error("Failed to save tracking");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    setIsUpdating(true);
    try {
      await onUpdate(order.id, { internal_notes: internalNotes });
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSendDispatchEmail = async () => {
    if (!session?.access_token) {
      toast.error("Session expired");
      return;
    }
    setIsSendingEmail(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-dispatch-email", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { 
          orderId: order.id,
          trackingNumber: trackingNumber || order.tracking_number,
          courier: courier || order.courier,
        },
      });

      if (error || data?.error) {
        throw new Error(data?.error || "Failed to send email");
      }

      toast.success("Dispatch email sent to customer");
    } catch (err: any) {
      toast.error(err.message || "Failed to send dispatch email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Calculate order age in days
  const getOrderAge = () => {
    const created = new Date(order.created_at);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  const orderAgeDays = getOrderAge();
  const isOverdue = !order.fulfilled && orderAgeDays >= 3;
  const needsTracking = order.fulfilled && !order.tracking_number;

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow popups to print");
      return;
    }

    const formatAddressHtml = (addr: DeliveryAddress) => {
      return [addr.line1, addr.line2, addr.city, addr.county, addr.postcode, addr.country]
        .filter(Boolean)
        .map(part => escapeHtml(part))
        .join("<br>");
    };

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Packing Slip - ${order.order_number}</title>
          <style>
            @page { size: A4; margin: 15mm; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; 
              padding: 0;
              color: #000;
              background: #fff;
              font-size: 11px;
              line-height: 1.4;
            }
            .slip { max-width: 100%; }
            .header {
              text-align: center;
              border-bottom: 3px solid #000;
              padding-bottom: 16px;
              margin-bottom: 16px;
            }
            .logo { font-size: 28px; font-weight: 700; letter-spacing: 4px; margin-bottom: 4px; }
            h1 { font-size: 14px; font-weight: 400; text-transform: uppercase; letter-spacing: 2px; }
            .order-info {
              display: flex;
              justify-content: space-between;
              background: #f5f5f5;
              padding: 10px 14px;
              margin-bottom: 16px;
              border: 1px solid #ddd;
            }
            .info-row { display: flex; gap: 6px; }
            .info-row .label { font-weight: 600; }
            .info-row .value { font-family: 'Courier New', monospace; }
            .section { margin-bottom: 16px; }
            h2 { 
              font-size: 10px; 
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              border-bottom: 1px solid #ddd;
              padding-bottom: 4px;
              margin-bottom: 8px;
            }
            .customer-name { font-size: 13px; font-weight: 600; margin-bottom: 3px; }
            .customer-contact { color: #444; font-size: 11px; }
            .address { line-height: 1.6; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { text-align: left; padding: 8px 6px; border-bottom: 1px solid #ddd; }
            th { 
              background: #f9f9f9; 
              font-weight: 600;
              text-transform: uppercase;
              font-size: 9px;
              letter-spacing: 0.5px;
            }
            .col-qty, .col-price, .col-total { text-align: right; width: 70px; }
            td.col-qty, td.col-price, td.col-total { 
              text-align: right; 
              font-family: 'Courier New', monospace;
            }
            .totals {
              margin-top: 12px;
              border-top: 2px solid #000;
              padding-top: 8px;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              padding: 4px 0;
              font-family: 'Courier New', monospace;
            }
            .total-row.grand-total {
              font-weight: 700;
              font-size: 13px;
              border-top: 1px solid #ddd;
              margin-top: 6px;
              padding-top: 8px;
            }
            .notes-section {
              background: #fffef0;
              border: 1px dashed #ccc;
              padding: 10px;
              margin-top: 16px;
            }
            .notes { font-style: italic; color: #555; font-size: 10px; }
            .footer {
              text-align: center;
              margin-top: 24px;
              padding-top: 16px;
              border-top: 1px solid #ddd;
            }
            .footer p { margin-bottom: 3px; font-size: 11px; }
            .footer .small { font-size: 9px; color: #888; }
            @media print { 
              body { padding: 0; } 
              .notes-section { background: #fff; }
            }
          </style>
        </head>
        <body>
          <div class="slip">
            <div class="header">
              <div class="logo">SAMBOARDS</div>
              <h1>Packing Slip</h1>
            </div>
            
            <div class="order-info">
              <div class="info-row">
                <span class="label">Order:</span>
                <span class="value">${order.order_number}</span>
              </div>
              <div class="info-row">
                <span class="label">Date:</span>
                <span class="value">${formatUKDateTime(order.created_at)}</span>
              </div>
            </div>
            
            <div class="section">
              <h2>Customer</h2>
              <div class="customer-name">${escapeHtml(order.customer_name)}</div>
              <div class="customer-contact">${escapeHtml(order.customer_email)}</div>
              ${order.customer_phone ? `<div class="customer-contact">${escapeHtml(order.customer_phone)}</div>` : ""}
            </div>
            
            <div class="section">
              <h2>Delivery Address</h2>
              <div class="address">${formatAddressHtml(order.delivery_address)}</div>
            </div>
            
            <div class="section">
              <h2>Items</h2>
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th class="col-qty">Qty</th>
                    <th class="col-price">Unit</th>
                    <th class="col-total">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${order.items.map((item) => `
                    <tr>
                      <td>${escapeHtml(item.name)}</td>
                      <td class="col-qty">${item.quantity}</td>
                      <td class="col-price">£${item.price.toFixed(2)}</td>
                      <td class="col-total">£${(item.price * item.quantity).toFixed(2)}</td>
                    </tr>
                  `).join("")}
                </tbody>
              </table>
              
              <div class="totals">
                <div class="total-row">
                  <span>Subtotal</span>
                  <span>£${order.subtotal.toFixed(2)}</span>
                </div>
                <div class="total-row">
                  <span>Shipping</span>
                  <span>£${order.shipping.toFixed(2)}</span>
                </div>
                <div class="total-row grand-total">
                  <span>Total</span>
                  <span>£${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            ${internalNotes ? `
            <div class="notes-section">
              <h2>Notes</h2>
              <div class="notes">${escapeHtmlWithBreaks(internalNotes)}</div>
            </div>
            ` : ""}
            
            <div class="footer">
              <p>Thank you for your order!</p>
              <p class="small">Samboards Ltd • www.samboards.co.uk</p>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const CopyButton = ({ text, field }: { text: string; field: string }) => (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0"
      onClick={() => copyToClipboard(text, field)}
    >
      {copiedField === field ? (
        <Check className="h-3.5 w-3.5 text-emerald-400" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-slate-400" />
      )}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader className="pb-3 border-b border-slate-700/50">
          <DialogTitle className="text-lg text-slate-100 flex items-center gap-2">
            <Package className="h-5 w-5 text-emerald-400" />
            Order {order.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 py-3">
          {/* Left Column - Order Details */}
          <div className="space-y-4">
            {/* Status Badges */}
            <div className="flex flex-wrap gap-1.5">
              <Badge
                variant={order.fulfilled ? "default" : "secondary"}
                className={
                  order.fulfilled
                    ? "bg-emerald-900/50 text-emerald-300 border-emerald-700 text-xs"
                    : "bg-amber-900/50 text-amber-300 border-amber-700 text-xs"
                }
              >
                {order.fulfilled ? (
                  <><CheckCircle2 className="h-3 w-3 mr-1" /> Fulfilled</>
                ) : "Unfulfilled"}
              </Badge>
              <Badge
                className={`text-xs ${
                  order.payment_status === "paid"
                    ? "bg-emerald-900/50 text-emerald-300 border-emerald-700"
                    : "bg-slate-700/50 text-slate-300 border-slate-600"
                }`}
              >
                {order.payment_status}
              </Badge>
              {isOverdue && (
                <Badge className="bg-red-900/50 text-red-300 border-red-700 text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Overdue ({orderAgeDays}d)
                </Badge>
              )}
              {needsTracking && (
                <Badge className="bg-blue-900/50 text-blue-300 border-blue-700 text-xs">
                  <Tag className="h-3 w-3 mr-1" />
                  Needs Tracking
                </Badge>
              )}
            </div>

            {/* Timestamps */}
            <div className="flex flex-wrap gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Ordered: {formatUKDateTime(order.created_at)}
              </span>
              {order.fulfilled_at && (
                <span className="flex items-center gap-1 text-emerald-400">
                  <CheckCircle2 className="h-3 w-3" />
                  Fulfilled: {formatUKDateTime(order.fulfilled_at)}
                </span>
              )}
            </div>

            {/* Customer Section */}
            <Card className="bg-slate-800/50 border-slate-700 p-3">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" /> Customer
              </h3>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-slate-100 font-medium text-sm">{order.customer_name}</span>
                  <CopyButton text={order.customer_name} field="name" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-slate-300">
                    <Mail className="h-3.5 w-3.5 text-slate-500" />
                    <span className="text-xs">{order.customer_email}</span>
                  </div>
                  <CopyButton text={order.customer_email} field="email" />
                </div>
                {order.customer_phone && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-slate-300">
                      <Phone className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-xs">{order.customer_phone}</span>
                    </div>
                    <CopyButton text={order.customer_phone} field="phone" />
                  </div>
                )}
              </div>
            </Card>

            {/* Shipping Label */}
            <Card className="bg-slate-800/50 border-slate-700 p-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> Shipping Label
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(generateShippingLabel(), "shippingLabel")}
                  className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/20 h-7 text-xs"
                >
                  {copiedField === "shippingLabel" ? (
                    <Check className="h-3 w-3 mr-1" />
                  ) : (
                    <Copy className="h-3 w-3 mr-1" />
                  )}
                  Copy
                </Button>
              </div>
              <div className="bg-white text-black p-3 rounded border-2 border-dashed border-slate-400 font-mono text-xs">
                <pre className="whitespace-pre-wrap leading-relaxed">
{generateShippingLabel()}
                </pre>
              </div>
            </Card>

            {/* Order Items */}
            <Card className="bg-slate-800/50 border-slate-700 p-3">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5" /> Items ({order.items.length})
              </h3>
              <div className="space-y-1.5">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-slate-200 text-xs">
                    <span>{item.quantity}x {item.name}</span>
                    <span>£{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <Separator className="bg-slate-700 my-1.5" />
                <div className="flex justify-between text-slate-400 text-xs">
                  <span>Subtotal</span>
                  <span>£{order.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-400 text-xs">
                  <span>Shipping</span>
                  <span>£{order.shipping.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-100 font-semibold text-sm">
                  <span>Total</span>
                  <span>£{order.total.toFixed(2)}</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Fulfilment Console */}
          <div className="space-y-4">
            {/* Fulfilment Card */}
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700 p-4">
              <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
                <Truck className="h-4 w-4 text-emerald-400" /> Fulfilment Console
              </h3>
              
              <div className="space-y-3">
                {/* Courier & Tracking */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Courier</Label>
                    <Select value={courier} onValueChange={setCourier}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-100 h-9 text-xs">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                        {COURIERS.map((c) => (
                          <SelectItem key={c.value} value={c.value} className="text-slate-100 text-xs">
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-400 text-xs">Tracking #</Label>
                    <Input
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder="Enter tracking..."
                      className="bg-slate-900 border-slate-700 text-slate-100 h-9 text-xs"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleSaveTracking}
                  disabled={isUpdating}
                  variant="outline"
                  size="sm"
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 h-8 text-xs"
                >
                  {isUpdating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Save Tracking Info
                </Button>

                <Separator className="bg-slate-700" />

                {/* Fulfil Button */}
                {!order.fulfilled ? (
                  <Button
                    onClick={handleFulfilOrder}
                    disabled={isUpdating}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-10"
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Fulfil Order
                  </Button>
                ) : (
                  <Button
                    onClick={handleMarkUnfulfilled}
                    disabled={isUpdating}
                    variant="outline"
                    className="w-full border-amber-700 text-amber-300 hover:bg-amber-900/20 h-10"
                  >
                    {isUpdating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Mark as Unfulfilled
                  </Button>
                )}

                {/* Send Dispatch Email */}
                <Button
                  onClick={handleSendDispatchEmail}
                  disabled={isSendingEmail}
                  variant="outline"
                  className="w-full border-cyan-700 text-cyan-400 hover:bg-cyan-900/20 h-9 text-xs"
                >
                  {isSendingEmail ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                  Send Dispatch Email
                </Button>
              </div>
            </Card>

            {/* Internal Notes */}
            <Card className="bg-slate-800/50 border-slate-700 p-3">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <StickyNote className="h-3.5 w-3.5" /> Internal Notes
              </h3>
              <Textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Add notes about this order..."
                className="bg-slate-900 border-slate-700 text-slate-100 min-h-[70px] resize-none text-xs"
              />
              <Button
                onClick={handleSaveNotes}
                disabled={isUpdating}
                variant="outline"
                size="sm"
                className="mt-2 border-slate-600 text-slate-300 hover:bg-slate-800 h-7 text-xs"
              >
                {isUpdating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Save Notes
              </Button>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-slate-800/50 border-slate-700 p-3">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Quick Actions</h3>
              <div className="grid grid-cols-3 gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-300 text-xs h-8"
                  onClick={() => copyToClipboard(order.customer_email, "quickEmail")}
                >
                  <Mail className="h-3 w-3 mr-1" /> Email
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-300 text-xs h-8"
                  onClick={() => copyToClipboard(generateShippingLabel(), "quickLabel")}
                >
                  <MapPin className="h-3 w-3 mr-1" /> Label
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-slate-600 text-slate-300 text-xs h-8"
                  onClick={handlePrint}
                >
                  <Printer className="h-3 w-3 mr-1" /> Print
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
